import {
	IconAlertTriangle,
	IconArrowUpCircle,
	IconBug,
	IconExternalLink,
	IconSparkles,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import type { allChangelogs } from "content-collections";
import {
	type ChangelogType,
	changelogTypeLabels,
	getChangelogTypeBadgeClass,
	getChangelogTypeCardClass,
	getChangelogTypeIconClass,
	getChangelogTypeIconWellClass,
} from "@/lib/changelog-type-styles";
import { cn } from "@/lib/utils";

type ChangeLogCardProps = {
	changelog: (typeof allChangelogs)[number];
};

const changelogIcons: Record<ChangelogType, typeof IconSparkles> = {
	feature: IconSparkles,
	improvement: IconArrowUpCircle,
	deprecation: IconAlertTriangle,
	"bug-fix": IconBug,
};

export const ChangeLogCard = ({ changelog }: ChangeLogCardProps) => {
	const type = changelog.type as ChangelogType;
	const Icon = changelogIcons[type];

	const cardContent = (
		<article
			key={changelog._meta.path}
			className={cn(
				"rounded-xl border text-sm transition-colors duration-200",
				"light:shadow-sm dark:hover:opacity-95",
				getChangelogTypeCardClass(type),
			)}
		>
			<div className="flex flex-col gap-3 px-4 py-4 sm:px-5">
				<div className="flex items-start gap-3.5 sm:gap-4">
					<div className={cn("size-10", getChangelogTypeIconWellClass(type))}>
						<Icon
							className={cn("size-[1.125rem]", getChangelogTypeIconClass(type))}
							aria-hidden
						/>
					</div>
					<div className="min-w-0 flex flex-1 flex-col gap-2">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 flex-col gap-2">
								<span className={getChangelogTypeBadgeClass(type)}>
									{changelogTypeLabels[type]}
								</span>
								<h3 className="text-base font-semibold leading-snug text-card-foreground">
									{changelog.title}
								</h3>
							</div>
							{changelog.url ? (
								<IconExternalLink
									className="mt-0.5 size-4 shrink-0 text-muted-foreground/70"
									aria-hidden
								/>
							) : null}
						</div>
						<p className="text-pretty text-sm text-muted-foreground">
							{changelog.summary}
						</p>
					</div>
				</div>
			</div>
		</article>
	);

	return changelog.url ? (
		<Link
			to={changelog.url}
			className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
		>
			{cardContent}
		</Link>
	) : (
		<div className="block cursor-default">{cardContent}</div>
	);
};
